import { useEffect, useState } from 'react';
import { Loader2, ClipboardList, Check } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  getPOBEntriesForWorkforce,
  type WorkforceFromPOB,
} from '@/services/db/pob';
import type { POBManifest } from '@/types/pob';
import { savePersonnelRow } from '@/services/db/dsrEntry';

interface Props {
  open: boolean;
  onClose: () => void;
  manifest: POBManifest;
  dsrId: string;
  tenantId: string;
  onImported: () => void;
}

// Legacy regex kept for backwards-compat with rows written by the old
// POB import path (S51–S68), which encoded the SSE ratio in `notes` as
// `[POB:sse=M/N] …`. S68b moved to a real `sse_count` column; the form
// summary prefers the column and falls back to this regex only when the
// column is NULL (i.e. the row was imported before the migration).
export const POB_NOTE_TAG_RE = /^\[POB:sse=(\d+)\/(\d+)\]/;

export default function POBImportModal({
  open,
  onClose,
  manifest,
  dsrId,
  tenantId,
  onImported,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<WorkforceFromPOB[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    getPOBEntriesForWorkforce(manifest.id)
      .then((g) => {
        setGroups(g);
        setSelected(new Set(g.map((x) => x.company)));
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : 'Failed to load POB')
      )
      .finally(() => setLoading(false));
  }, [open, manifest.id]);

  if (!open) return null;

  const toggle = (company: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(company)) next.delete(company);
      else next.add(company);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(groups.map((g) => g.company)));
  const deselectAll = () => setSelected(new Set());

  const doImport = async () => {
    setImporting(true);
    setError(null);
    try {
      for (const g of groups) {
        if (!selected.has(g.company)) continue;
        const sseNames = g.entries
          .filter((e) => e.is_sse)
          .map((e) => `${e.first_name} ${e.last_name}`);
        const human = [
          `Imported from POB (${manifest.manifest_date})`,
          `${g.totalCount} personnel`,
          sseNames.length ? `SSE: ${sseNames.join(', ')}` : null,
        ]
          .filter(Boolean)
          .join(' · ');
        // Why: supervisor_name is left null so the user enters their own
        // SSE name during the DSR. sse_count captures the POB count so
        // the summary doesn't depend on anyone typing a supervisor name.
        await savePersonnelRow({
          dsrReportId: dsrId,
          tenantId,
          companyName: g.company,
          crewType: 'contractor',
          personnelCount: g.totalCount,
          sseCount: g.sseCount,
          supervisorName: null,
          notes: human,
        });
      }
      onImported();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const totalSelected = groups
    .filter((g) => selected.has(g.company))
    .reduce((s, g) => s + g.totalCount, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="border-b px-6 py-4">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-mozaic-teal" />
            <h2 className="font-semibold text-mozaic-navy">
              Import from POB —{' '}
              {format(new Date(manifest.manifest_date + 'T00:00:00'), 'MMM d, yyyy')}
            </h2>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {manifest.facility_name ?? manifest.projects?.name ?? 'Facility'} —{' '}
            {manifest.total_pob ?? 0} people on board
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-mozaic-teal" />
            </div>
          )}
          {error && <p className="text-sm text-mozaic-red">{error}</p>}
          {!loading && !error && groups.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No active POB entries to import.
            </p>
          )}
          {!loading && groups.length > 0 && (
            <>
              <div className="mb-2 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  {groups.length} companies · {selected.size} selected
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={selectAll}
                    className="text-mozaic-teal hover:underline"
                  >
                    Select all
                  </button>
                  <span className="text-muted-foreground">·</span>
                  <button
                    type="button"
                    onClick={deselectAll}
                    className="text-mozaic-teal hover:underline"
                  >
                    Deselect all
                  </button>
                </div>
              </div>
              <ul className="divide-y">
                {groups.map((g) => {
                  const isSelected = selected.has(g.company);
                  return (
                    <li key={g.company}>
                      <button
                        type="button"
                        onClick={() => toggle(g.company)}
                        className="flex w-full items-start gap-3 py-3 text-left hover:bg-muted/50"
                      >
                        <div
                          className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border ${
                            isSelected
                              ? 'border-mozaic-teal bg-mozaic-teal text-white'
                              : 'border-input bg-background'
                          }`}
                        >
                          {isSelected && <Check className="h-3 w-3" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate font-medium text-mozaic-navy">
                              {g.company}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {g.totalCount} people
                            </span>
                            {g.sseCount > 0 && (
                              <Badge className="bg-amber-500 text-white">
                                {g.sseCount} SSE
                              </Badge>
                            )}
                          </div>
                          <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                            {g.entries.slice(0, 4).map((e) => (
                              <li key={e.id} className="truncate">
                                {e.last_name}, {e.first_name} — {e.position_title}
                                {e.is_sse && ' · SSE'}
                              </li>
                            ))}
                            {g.entries.length > 4 && (
                              <li>+ {g.entries.length - 4} more</li>
                            )}
                          </ul>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t px-6 py-4">
          <span className="text-xs text-muted-foreground">
            {totalSelected} personnel across {selected.size} companies
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={importing}>
              Cancel
            </Button>
            <Button
              className="bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
              onClick={doImport}
              disabled={importing || selected.size === 0}
            >
              {importing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                `Import Selected Companies (${selected.size})`
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
